// src/infra/api/Coletas.js
import axiosInstance from '../axios';

/**
 * Páginas de coleta do plano LEITE (leite, colostro, água): o registro das amostras,
 * a configuração de qual análise cada página mostra e o cadastro de pontos de coleta.
 *
 * `pagina` é a chave do backend: 'LEITE' | 'COLOSTRO' | 'AGUA'.
 */

// ── Amostras da página ──

/**
 * Registra uma amostra. Informe `torreId` (aguarda o tubo) OU `experimentId`
 * (experimento que nasceu sem registro).
 */
export async function registrarColeta(payload) {
    const res = await axiosInstance.post('/coletas', payload);
    return res.data;
}

/** Busca paginada (page 0-based). Responde `{ items, totalElements, totalPages, page, size }`. */
export async function buscarColetas(filtro, page = 0, size = 20) {
    const res = await axiosInstance.post('/coletas/search', filtro, {
        params: { page, size },
    });
    return res.data;
}

/**
 * Última amostra da página COM resultado (linha do histórico, com a variação e a data da
 * coleta anterior comparável). null enquanto não há resultado (204).
 */
export async function ultimaColeta(pagina, { pontoColetaId, origem } = {}) {
    const res = await axiosInstance.get('/coletas/ultima', { params: { pagina, pontoColetaId, origem } });
    return res.status === 204 ? null : res.data || null;
}

/** Detalhe de uma amostra: a linha do histórico, nomes, processamento e observações. */
export async function buscarColeta(id) {
    const res = await axiosInstance.get(`/coletas/${id}`);
    return res.data;
}

/**
 * Exclui a amostra (exclusão lógica da análise; o backend recusa se o ensaio estiver
 * rodando). O motivo é obrigatório.
 */
export async function excluirColeta(id, motivo) {
    await axiosInstance.delete(`/amostras/${id}`, { params: { motivo } });
}

/** Pontos do gráfico de evolução no período (datas ISO). */
export async function serieColetas(pagina, de, ate) {
    const res = await axiosInstance.get('/coletas/serie', {
        params: { pagina, de, ate },
    });
    return res.data;
}

/** Origens já digitadas na página, para sugerir no campo de texto livre. */
export async function origensColeta(pagina) {
    const res = await axiosInstance.get('/coletas/origens', { params: { pagina } });
    return res.data;
}

/** Experimentos do protocolo da página que nasceram sem registro. */
export async function experimentosSemRegistro(pagina) {
    const res = await axiosInstance.get('/coletas/sem-registro', { params: { pagina } });
    return res.data;
}

export async function atualizarDadosColeta(id, dados) {
    const res = await axiosInstance.put(`/coletas/${id}/coleta`, dados);
    return res.data;
}

/** Cancela um registro que ainda aguarda o tubo. */
export async function cancelarColeta(id) {
    await axiosInstance.post(`/coletas/${id}/cancelar`);
}

// ── Configuração das páginas (ADMIN) ──

export async function listarPaginasColeta() {
    const res = await axiosInstance.get('/paginas-coleta');
    return res.data;
}

export async function configurarPaginaColeta(pagina, { analiseId, alvo, unidade, bacteriaId }) {
    const res = await axiosInstance.put(`/paginas-coleta/${pagina}`, {
        analiseId,
        alvo,
        unidade,
        bacteriaId: bacteriaId || null,
    });
    return res.data;
}

// ── Referências da página (legislação e metas da propriedade) ──

/**
 * Linhas de referência do gráfico de evolução e o limite que o assistente usa. Responde
 * `{ pagina, unidade, itens: [{ id, tipo, nome, valor, unidade, fonte, descricao, link,
 * exibir, principal }], atualizadoEm, atualizadoPor }`. `tipo`: 'LEGISLACAO' (valor da
 * norma, fixo) ou 'META_INTERNA' (definida pela propriedade).
 */
export async function referenciasPagina(pagina) {
    const res = await axiosInstance.get(`/paginas-coleta/${pagina}/referencias`);
    return res.data;
}

/**
 * Salva as escolhas da conta (ADMIN/GESTOR). `itens`: legislação só com `exibir`; metas
 * com nome/valor/descrição (meta nova pode ir com id provisório). `principal`: id da
 * referência do assistente, ou null para nenhuma.
 */
export async function salvarReferenciasPagina(pagina, { itens, principal }) {
    const res = await axiosInstance.put(`/paginas-coleta/${pagina}/referencias`, { itens, principal: principal || null });
    return res.data;
}

// ── Pontos de coleta ──

export async function listarPontosColeta({ analiseId, incluirInativos = false } = {}) {
    const res = await axiosInstance.get('/pontos-coleta', {
        params: { analiseId: analiseId || undefined, incluirInativos },
    });
    return res.data;
}

export async function criarPontoColeta(ponto) {
    const res = await axiosInstance.post('/pontos-coleta', ponto);
    return res.data;
}

export async function atualizarPontoColeta(id, ponto) {
    const res = await axiosInstance.put(`/pontos-coleta/${id}`, ponto);
    return res.data;
}

/** Desativa (o backend mantém o registro: amostras antigas apontam para ele). */
export async function desativarPontoColeta(id) {
    await axiosInstance.delete(`/pontos-coleta/${id}`);
}

/** Volta a oferecer um ponto desativado. 409 se outro ponto ativo já usa o nome. */
export async function reativarPontoColeta(id) {
    const res = await axiosInstance.post(`/pontos-coleta/${id}/reativar`);
    return res.data;
}

/** Mensagem do backend (GlobalExceptionHandler) ou o texto de reserva. */
export const mensagemErro = (erro, reserva) =>
    erro?.response?.data?.message || reserva;
